import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  setBusinessConfig,
  updateBusinessField,
  resetBusinessConfig,
  setLoading,
  setSubmitting,
  setError
} from '../../store/slices/business/businessConfigSlice';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import BusinessConfigService from '../../services/business/BusinessConfigService';
import { BusinessConfigurationForm } from '../../types/business/BusinessTypes';
import useApiErrorHandler from '../useApiErrorHandler';

export const useBusinessConfig = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { form, isLoading, isSubmitting, error } = useSelector(
    (state: RootState) => state.businessConfig
  );
  const { handle } = useApiErrorHandler();
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

  const loadBusinessConfig = async () => {
    try {
      dispatch(setLoading(true));
      showLoadingIndicator();

      const response = await BusinessConfigService.getConfiguration();
      if (response.statusCode === 200) {
        dispatch(setBusinessConfig(response.data)); // Solo pasamos los datos del backend tal cual
      } else {
        dispatch(setError('No se pudo cargar la configuración.'));
        showAlert('error', 'Error', 'No se pudo obtener los datos de configuración.');
      }
    } catch (error: any) {
      handle(error); // Errores de red, validaciones, etc.
    } finally {
      dispatch(setLoading(false));
      hideLoadingIndicator();
    }
  };


  const saveBusinessConfig = async (
    form: BusinessConfigurationForm,
    logoFile?: File,
    pdfLogoFile?: File
  ) => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      // 🔹 Copiamos el formulario plano
      const payload = { ...form };

      let response;
      if (form.id) {
        // ✅ Actualizar configuración existente con ambos logos opcionales
        response = await BusinessConfigService.updateConfiguration(
          form.id,
          payload,
          logoFile,
          pdfLogoFile
        );
      } else {
        // ✅ Crear nueva configuración con ambos logos opcionales
        response = await BusinessConfigService.createConfiguration(
          payload,
          logoFile,
          pdfLogoFile
        );
      }

      // 🔹 Manejo de respuesta
      if (response.statusCode === 200 || response.statusCode === 201) {
        showAlert(response.type, response.title, response.message);
        dispatch(setBusinessConfig(response.data));
      } else {
        showAlert('error', 'Error', 'No se pudo guardar la configuración.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setSubmitting(false));
      hideLoadingIndicator();
    }
  };






  /**
   * ✅ Obtiene el logo de la empresa a partir del nombre de archivo.
   */
  const handlerGetBusinessLogo = async (filename: string): Promise<string | null> => {
    try {
      const imageUrl = await BusinessConfigService.getBusinessLogoUrl(filename);
      return imageUrl; // Devuelve la URL lista para usar como src en una imagen
    } catch (error) {
      console.error("Error obteniendo el logo de la empresa:", error);
      return null;
    }
  };


  const updateField = <K extends keyof BusinessConfigurationForm>(
    field: K,
    value: BusinessConfigurationForm[K]
  ) => {
    dispatch(updateBusinessField({ field, value }));
  };

  const resetForm = () => {
    dispatch(resetBusinessConfig());
  };

  return {
    form,
    isLoading,
    isSubmitting,
    error,
    updateField,
    resetForm,
    loadBusinessConfig,
    saveBusinessConfig, // ⬅️ Ya preparado para recibir el logoFile
    handlerGetBusinessLogo,
  };
};
